import { Link } from "@/components/ui/link";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslations } from "@/hooks/use-translations";
import { useTransition } from "react";
import {
	House,
	FolderKanban,
	BarChart3,
	Images,
	LayoutGrid,
	Settings,
	Plug,
	LogOut,
	PanelLeftClose,
	PanelLeftOpen,
	createLucideIcon,
	type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/ui/logo";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/ui/language-switcher";
import { useAuth } from "@/hooks/use-auth";
import { useActiveTeam } from "@/hooks/use-team";
import { useDoohAccess } from "@/hooks/use-dooh-access";
import { useAccess } from "@/hooks/use-access";

// Keep the SEO and signage symbols with the same rendering as the app's Lucide icons.
const SeoNavigationIcon = createLucideIcon("seo-navigation", [
	["path", { d: "M11 3a8 8 0 1 0 8 8M16.66 16.66 21 21", key: "search" }],
	[
		"path",
		{
			d: "M17 1.5c.7 2.1 1.9 3.3 4 4-2.1.7-3.3 1.9-4 4-.7-2.1-1.9-3.3-4-4 2.1-.7 3.3-1.9 4-4Z",
			fill: "currentColor",
			stroke: "none",
			key: "sparkle",
		},
	],
]);

const DoohNavigationIcon = createLucideIcon("dooh-navigation", [
	["rect", { x: "3", y: "3", width: "18", height: "13", rx: "1", key: "sign" }],
	["path", { d: "M12 16v6", key: "stand" }],
]);

interface NavItem {
	href: string;
	labelKey: string;
	icon: LucideIcon;
	// Same rule as the route in App.tsx: an item shows only if its page opens.
	visible: (access: ReturnType<typeof useAccess>) => boolean;
}

const navItems: NavItem[] = [
	{ href: "/home", labelKey: "home", icon: House, visible: (a) => a.has("projects") },
	{ href: "/dashboard", labelKey: "dashboard", icon: BarChart3, visible: (a) => a.has("projects") },
	{ href: "/projects", labelKey: "projects", icon: FolderKanban, visible: (a) => a.has("projects") },
	{ href: "/analyzer", labelKey: "analyzer", icon: SeoNavigationIcon, visible: (a) => a.has("seo") },
	{ href: "/media", labelKey: "media", icon: Images, visible: (a) => a.hasAny("projects", "seo") },
];

const mockDoohEnabled =
	import.meta.env.DEV && import.meta.env.VITE_MOCK_DOOH === "true";

interface SidebarProps {
	collapsed: boolean;
	onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
	const location = useLocation();
	const pathname = location.pathname;
	const navigate = useNavigate();
	const t = useTranslations("nav");
	const tCommon = useTranslations("common");
	const { signOut, user } = useAuth();
	const [isPending, startTransition] = useTransition();
	const { activeTeam } = useActiveTeam();
	const access = useAccess();
	// Ask the DOOH service only when the user has the DOOH feature at all.
	const { allowed: doohServiceAllows } = useDoohAccess(
		mockDoohEnabled || !access.has("dooh") ? undefined : user?.id,
	);
	// DOOH needs the feature, and the DOOH service checks on its own as well.
	const hasDoohAccess = access.has("dooh") && doohServiceAllows;
	// Super Admin only, the same rule as the Manager page and /api/admin.
	const isManager = access.isSuperAdmin;

	// Same active styling for every nav item (GUIDE rule 6), incl. Manager
	// Panel and Settings.
	const isPathActive = (href: string) =>
		pathname === href || pathname.startsWith(`${href}/`);
	const navItemClassName = (active: boolean) =>
		active
			? "bg-nav-active-bg text-text-primary font-semibold"
			: "text-text-secondary hover:bg-bg-canvas hover:text-text-primary";
	const navIconClassName = (active: boolean) =>
		active
			? "text-text-primary"
			: "text-text-secondary group-hover:text-text-primary";

	const handleNavigation = (href: string) => {
		startTransition(() => {
			navigate(href);
		});
	};

	return (
		<aside
			className="fixed left-0 top-0 z-40 flex h-screen flex-col bg-bg-surface border-r border-border-subtle transition-[width] duration-200 motion-reduce:transition-none"
			style={{ width: "var(--sidebar-width, 240px)" }}
		>
			{/* Logo */}
			<div
				className={cn(
					"flex shrink-0 items-center",
					collapsed
						? "h-[96px] flex-col justify-center gap-2 px-3"
						: "h-[64px] justify-between px-5",
				)}
			>
				<Link
					href={access.homePath}
					title={collapsed ? "minora ai" : undefined}
					className="flex items-center"
				>
					{collapsed ? (
						<Logo size={32} />
					) : (
						<img src="/login_logo.svg" alt="minora ai" className="h-8 w-auto" />
					)}
				</Link>
				<Button
					type="button"
					variant="ghost"
					size="icon-sm"
					onClick={onToggle}
					aria-label={t(collapsed ? "expandSidebar" : "collapseSidebar")}
					title={t(collapsed ? "expandSidebar" : "collapseSidebar")}
					aria-expanded={!collapsed}
					aria-controls="dashboard-navigation"
					className="text-text-tertiary"
				>
					{collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
				</Button>
			</div>

			{/* Workspace Selector - hidden
      <div className="px-3 pt-2 pb-1">
        <button
          onClick={() => handleNavigation("/workspace/main")}
          className="w-full flex items-center gap-3 rounded-xl bg-bg-canvas border border-border-subtle px-3 py-3 hover:border-border-control transition-colors"
        >
          <div className="w-9 h-9 rounded-full bg-bg-surface flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-text-primary">W</span>
          </div>
          <span className="text-sm font-medium text-text-primary flex-1 text-left truncate">
            Workspace
          </span>
          <Layers size={16} className="text-text-tertiary flex-shrink-0" />
        </button>
      </div>
      */}

			{/* Navigation */}
			<nav
				id="dashboard-navigation"
				className="flex-1 overflow-y-auto px-3 py-3 scrollbar-thin"
			>
				<div className="space-y-1">
					{navItems.filter((item) => item.visible(access)).map((item) => {
						const isActive = isPathActive(item.href);
						const Icon = item.icon;

						return (
							<Link
								key={item.href}
								href={item.href}
								title={collapsed ? t(item.labelKey) : undefined}
								aria-current={isActive ? "page" : undefined}
								onClick={(e) => {
									e.preventDefault();
									handleNavigation(item.href);
								}}
								className={cn(
									"flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all group",
									navItemClassName(isActive),
									collapsed && "justify-center px-0",
								)}
							>
								<Icon
									size={18}
									className={cn("flex-shrink-0", navIconClassName(isActive))}
								/>
								<span className={collapsed ? "sr-only" : undefined}>{t(item.labelKey)}</span>
							</Link>
						);
					})}

					{mockDoohEnabled ? (
						<a
							aria-disabled
							title="DOOH — предпросмотр иконки"
							className={cn(
								"flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-text-secondary hover:bg-bg-canvas hover:text-text-primary group",
								collapsed && "justify-center px-0",
							)}
						>
							<DoohNavigationIcon
								size={18}
								className="shrink-0 text-text-secondary group-hover:text-text-primary"
							/>
							<span className={collapsed ? "sr-only" : undefined}>DOOH</span>
						</a>
					) : (
						hasDoohAccess && (
							<Link
								href="/out-of-home"
								title={collapsed ? "DOOH" : undefined}
								aria-current={isPathActive("/out-of-home") ? "page" : undefined}
								onClick={(e) => {
									e.preventDefault();
									handleNavigation("/out-of-home");
								}}
								className={cn(
									"flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all group",
									navItemClassName(isPathActive("/out-of-home")),
									collapsed && "justify-center px-0",
								)}
							>
								<DoohNavigationIcon
									size={18}
									className={cn("shrink-0", navIconClassName(isPathActive("/out-of-home")))}
								/>
								<span className={collapsed ? "sr-only" : undefined}>DOOH</span>
							</Link>
						)
					)}

					{/* Website Builder: closed to everyone for now (not working yet). */}
				</div>
			</nav>

			{/* Footer */}
			<div className="border-t border-border-subtle p-3 space-y-1">
				<LanguageSwitcher collapsed={collapsed} />

				<div className="mt-2 pt-2 border-t border-border-subtle space-y-1">
					{/* Manager Panel — only visible to the authorised manager */}
					{isManager && (
						<Link
							href="/manager"
							title={collapsed ? t("addUser") : undefined}
							className={cn(
								"flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors group",
								navItemClassName(isPathActive("/manager")),
								collapsed && "justify-center px-0",
							)}
						>
							<LayoutGrid
								size={18}
								className={cn("shrink-0", navIconClassName(isPathActive("/manager")))}
							/>
							<span className={collapsed ? "sr-only" : undefined}>{t("addUser")}</span>
						</Link>
					)}
					{/* Admin link — hidden
					<Link
						href="/admin"
						className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-text-secondary transition-colors hover:bg-bg-canvas hover:text-text-primary group"
					>
						<ShieldCheck
							size={18}
							className="text-text-tertiary group-hover:text-text-primary"
						/>
						<span>{t("admin")}</span>
					</Link>
				*/}
					<Link
						href="/connectors"
						title={collapsed ? t("connectors") : undefined}
						className={cn(
							"flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors group",
							navItemClassName(isPathActive("/connectors")),
							collapsed && "justify-center px-0",
						)}
					>
						<Plug
							size={18}
							className={cn("shrink-0", navIconClassName(isPathActive("/connectors")))}
						/>
						<span className={collapsed ? "sr-only" : undefined}>{t("connectors")}</span>
					</Link>
					<Link
						href="/settings"
						title={collapsed ? t("settings") : undefined}
						className={cn(
							"flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors group",
							navItemClassName(isPathActive("/settings")),
							collapsed && "justify-center px-0",
						)}
					>
						<Settings
							size={18}
							className={cn("shrink-0", navIconClassName(isPathActive("/settings")))}
						/>
						<span className={collapsed ? "sr-only" : undefined}>{t("settings")}</span>
					</Link>
					<button
						type="button"
						onClick={() => signOut()}
						title={collapsed ? t("logout") : undefined}
						className={cn(
							"flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-text-secondary transition-colors hover:bg-danger/10 hover:text-danger group",
							collapsed && "justify-center px-0",
						)}
					>
						<LogOut
							size={18}
							className="shrink-0 text-text-secondary group-hover:text-danger"
						/>
						<span className={collapsed ? "sr-only" : undefined}>{t("logout")}</span>
					</button>
				</div>
			</div>
		</aside>
	);
}
